import React from 'react';
import {
    DateInputWithPickerField,
    RestrictedLengthTextAreaField,
    ThemeTokens,
} from '@acp/core-ui';
import {
    Button, ModalBody, ModalFooter, Spacer, VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { PortfolioPropertyTag, LeaseType } from '@acp/shared';
import { TenancyStep } from '../tenancy-steps';
import { MAX_TENANCY_NOTES_LENGTH } from '../schemas/contact-number-schema';
import { FormType, FormValues } from '../schemas/form-schema';
import { useProperty } from '../../../property/hooks/use-property';

interface LeaseAgreementScreenProps {
    onSetScreen: (step: TenancyStep) => void;
    handleClose: () => void;
}

export function LeaseAgreementScreen({
    onSetScreen,
    handleClose,
}: LeaseAgreementScreenProps) {
    const {
        control,
        handleSubmit,
        getValues,
        setValue,
    } = useFormContext<FormValues>();
    const formValues = getValues();

    React.useEffect(() => {
        if (FormType.Add === formValues.tenancy.formType) {
            setValue('tenancy.leaseType', LeaseType.New);
        }
    }, [formValues.tenancy.formType, setValue]);

    const propertyQuery = useProperty(formValues.propertyId);
    const tags = propertyQuery.data?.tags ?? [];
    const isPropertyIntegrated = tags.includes(PortfolioPropertyTag.Integrated);

    return (
        <>
            <ModalBody minH="lg" px={ThemeTokens.TenancyModal.body.py}>
                <VStack gap={6} alignItems="start" pb={6}>
                    <DateInputWithPickerField
                        control={control}
                        name="tenancy.startDate"
                        label="Tenancy start date"
                        allowClear={false}
                        isRequired
                    />
                    <DateInputWithPickerField
                        control={control}
                        name="tenancy.endDate"
                        label="Tenancy end date"
                    />
                    <DateInputWithPickerField
                        control={control}
                        name="tenancy.renewalDate"
                        label="Renewal date"
                        isRequired={false}
                    />
                    <DateInputWithPickerField
                        control={control}
                        name="tenancy.vacateDate"
                        label="Vacate date"
                    />
                    <RestrictedLengthTextAreaField
                        control={control}
                        name="tenancy.notes"
                        label="Notes"
                        placeholder="Add any notes regarding this lease agreement"
                        maxLength={MAX_TENANCY_NOTES_LENGTH}
                    />
                </VStack>
            </ModalBody>
            <ModalFooter>
                <Button
                    size="md"
                    colorScheme="muted"
                    variant="ghost"
                    onClick={() => handleClose()}
                >
                    Close
                </Button>
                <Spacer />
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    isDisabled={isPropertyIntegrated}
                    onClick={handleSubmit(() => onSetScreen(TenancyStep.Occupants))}
                >
                    Next
                </Button>
            </ModalFooter>
        </>
    );
}
